/**
 * Il menu che si apre dal bollino con le iniziali, in alto a destra.
 *
 * Sostituisce la fila «bollino + nome + Esci»: il nome per esteso e l'uscita
 * stavano sempre in vista pur servendo una volta al giorno, e occupavano lo
 * spazio dove ora si possono aggiungere le voci che verranno.
 *
 * È un menu vero, non un riquadro che compare: `role="menu"`, voci navigabili
 * con le frecce, chiusura con Esc o con un clic fuori, e il focus che torna
 * sul bollino. Chi lo apre da tastiera deve poterlo anche chiudere da
 * tastiera senza restare bloccato dentro.
 *
 * Le icone sono disegnate qui sotto in SVG, come il marchio: nessuna libreria
 * di icone per cinque disegni.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';

import type { Utente } from '../api/tipi-dati.ts';
import { useAutenticazione } from '../auth/contesto.tsx';
import { Icona } from './Icona.tsx';
import { InterruttoreNotifiche } from './InterruttoreNotifiche.tsx';
import { iniziali } from './formato.ts';

/** Le voci del menu, comprese quelle disabilitate: le frecce le attraversano tutte. */
const SELETTORE_VOCI = '[role="menuitem"], [role="menuitemcheckbox"]';

export function MenuUtente({ utente }: { utente: Utente }) {
  const { esci, ricarica } = useAutenticazione();

  const [aperto, setAperto] = useState(false);
  const contenitore = useRef<HTMLDivElement>(null);
  const tendina = useRef<HTMLDivElement>(null);
  const bollino = useRef<HTMLButtonElement>(null);

  /**
   * Chiude e riporta il focus sul bollino.
   *
   * Senza, chi ha aperto il menu da tastiera si ritroverebbe il focus sul
   * `<body>` — cioè da nessuna parte — e dovrebbe ripercorrere tutta
   * l'intestazione con Tab per tornare dov'era. Il clic fuori invece chiude
   * senza passare di qui: lì il focus lo sta già spostando l'utente.
   */
  function chiudi() {
    setAperto(false);
    bollino.current?.focus();
  }

  // Clic fuori. `pointerdown` e non `click`: il contenitore comprende anche il
  // bollino, così il suo `onClick` continua a fare da interruttore invece di
  // riaprire un menu appena chiuso da qui.
  useEffect(() => {
    if (!aperto) return;

    const alClicFuori = (evento: PointerEvent) => {
      if (!contenitore.current?.contains(evento.target as Node)) setAperto(false);
    };

    document.addEventListener('pointerdown', alClicFuori);
    return () => document.removeEventListener('pointerdown', alClicFuori);
  }, [aperto]);

  // All'apertura il focus va sulla prima voce: è quello che fanno i menu di
  // sistema, e senza, la freccia giù non avrebbe un punto da cui partire.
  useEffect(() => {
    if (!aperto) return;
    tendina.current?.querySelector<HTMLElement>(SELETTORE_VOCI)?.focus();
  }, [aperto]);

  function allaTastiera(evento: KeyboardEvent<HTMLDivElement>) {
    if (evento.key === 'Escape') {
      if (!aperto) return;
      evento.preventDefault();
      chiudi();
      return;
    }

    if (!aperto) {
      if (evento.key === 'ArrowDown') {
        evento.preventDefault();
        setAperto(true);
      }
      return;
    }

    // Tab porta il focus fuori dal menu: lasciarlo aperto alle spalle di chi
    // se n'è andato è il modo migliore per farlo cliccare a vuoto dopo.
    if (evento.key === 'Tab') {
      setAperto(false);
      return;
    }

    const voci = Array.from(tendina.current?.querySelectorAll<HTMLElement>(SELETTORE_VOCI) ?? []);
    if (voci.length === 0) return;
    const corrente = voci.indexOf(document.activeElement as HTMLElement);

    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      voci[corrente < 0 ? 0 : (corrente + 1) % voci.length]?.focus();
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      voci[corrente <= 0 ? voci.length - 1 : corrente - 1]?.focus();
    } else if (evento.key === 'Home') {
      evento.preventDefault();
      voci[0]?.focus();
    } else if (evento.key === 'End') {
      evento.preventDefault();
      voci[voci.length - 1]?.focus();
    }
  }


  return (
    <div className="menu-utente" ref={contenitore} onKeyDown={allaTastiera}>
      <button
        ref={bollino}
        type="button"
        className="avatar avatar-bottone"
        aria-haspopup="menu"
        aria-expanded={aperto}
        // Da bottone non può più essere `aria-hidden`: due lettere sarebbero
        // un comando senza nome. Il nome per esteso non è più scritto accanto,
        // quindi lo si dice qui.
        aria-label={`${utente.nome} — menu del tuo account`}
        onClick={() => (aperto ? chiudi() : setAperto(true))}
      >
        {iniziali(utente.nome)}
      </button>

      {aperto && (
        <div className="menu-tendina" ref={tendina}>
          <div className="menu-testata" id="menu-utente-saluto">
            <div className="menu-saluto">Ciao, {utente.nome}</div>
            <div className="menu-email">{utente.email}</div>
          </div>

          <div role="menu" aria-labelledby="menu-utente-saluto">
            {/* Il menu si chiude prima di navigare: lasciarlo aperto sopra la
                pagina appena arrivata lo farebbe sembrare parte di quella. */}
            <Link
              to="/profilo"
              role="menuitem"
              className="menu-voce"
              onClick={() => setAperto(false)}
            >
              <IconaPersona />
              <span className="menu-testo">Profilo</span>
            </Link>

            <Link
              to="/attivita"
              role="menuitem"
              className="menu-voce"
              onClick={() => setAperto(false)}
            >
              <IconaAttivita />
              <span className="menu-testo">Attività</span>
            </Link>

            {/* «Impostazioni» non sta più qui: dal 7 agosto è un ingranaggio
                nell'intestazione, accanto alla data. Questo menu raccoglie le
                cose che riguardano *la tua persona* — il tuo profilo, quello
                che hai fatto tu, la tua casella di posta — mentre le
                impostazioni valgono per tutta l'applicazione e per tutti.
                Tenerle qui dentro le faceva sembrare tue. */}

            <hr className="menu-divisore" />

            <InterruttoreNotifiche utente={utente} ricarica={ricarica} />

            <hr className="menu-divisore" />

            <button
              type="button"
              role="menuitem"
              className="menu-voce menu-esci"
              onClick={() => { setAperto(false); void esci(); }}
            >
              <IconaUscita />
              <span className="menu-testo">Esci</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Icone ----------------------------------------------------------------
 *
 * L'involucro comune sta in `Icona.tsx`: se n'è andato di qui quando
 * l'interruttore delle notifiche è diventato un componente condiviso e si è
 * portato dietro la campana. Qui restano i disegni che usa solo questo menu.
 */

function IconaPersona() {
  return (
    <Icona>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20c0-3.5 3.3-5.4 7.2-5.4s7.2 1.9 7.2 5.4" />
    </Icona>
  );
}

function IconaAttivita() {
  return (
    <Icona>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 7.1V12l3.3 2" />
    </Icona>
  );
}

function IconaUscita() {
  return (
    <Icona>
      <path d="M14.4 4.6h3a2 2 0 0 1 2 2v10.8a2 2 0 0 1-2 2h-3" />
      <path d="M9.8 8.2 6 12l3.8 3.8" />
      <path d="M6 12h9" />
    </Icona>
  );
}
